import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { BehaviorSubject } from 'rxjs';

export interface Notification {
    id: string;
    title: string;
    body: string;
    type: 'whatsapp' | 'ticket' | 'other';
    data?: any;
    timestamp: Date;
    read: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class FcmService {
    private app: FirebaseApp | null = null;
    private messaging: Messaging | null = null;
    private currentToken: string | null = null;

    notifications$ = new BehaviorSubject<Notification[]>([]);
    unreadCount$ = new BehaviorSubject<number>(0);

    constructor(private http: HttpClient) { }

    /**
     * Initialize Firebase and request notification permission
     */
    async initialize(): Promise<void> {
        try {
            // Check if browser supports notifications
            if (!('Notification' in window)) {
                console.log('This browser does not support notifications');
                return;
            }

            // Initialize Firebase
            this.app = initializeApp(environment.firebaseConfig);
            this.messaging = getMessaging(this.app);

            // Request permission
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.log('Notification permission denied');
                return;
            }

            // Get FCM token
            await this.getAndRegisterToken();

            // Listen for foreground messages
            this.setupForegroundListener();

            // Load saved notifications from localStorage
            this.loadSavedNotifications();

        } catch (error) {
            console.error('Error initializing FCM:', error);
        }
    }

    /**
     * Get FCM token and register with backend
     */
    private async getAndRegisterToken(): Promise<void> {
        if (!this.messaging) return;

        try {
            // Get token - requires vapidKey for web push
            const token = await getToken(this.messaging, {
                vapidKey: 'BK9kp-Ttf26nIURdRR-B8-9jHT6tLHXouWv_C6NPOBpFK5dIXRMWP67lAH_INTEIScPANv374Ej1nHsY-uMDQU8'
            });

            if (token) {
                this.currentToken = token;
                await this.registerToken(token);
                console.log('FCM Token registered');
            }
        } catch (error) {
            console.error('Error getting FCM token:', error);
        }
    }

    /**
     * Register token with backend
     */
    private async registerToken(token: string): Promise<void> {
        const deviceInfo = `${navigator.userAgent.substring(0, 100)}`;

        this.http.post(`${environment.apiUrl}/fcm-tokens/register.json`, {
            token,
            device_info: deviceInfo
        }).subscribe({
            next: () => console.log('Token registered with backend'),
            error: (err) => console.error('Error registering token:', err)
        });
    }

    /**
     * Setup listener for foreground messages
     */
    private setupForegroundListener(): void {
        if (!this.messaging) return;

        onMessage(this.messaging, (payload) => {
            console.log('Foreground message received:', payload);

            const notification: Notification = {
                id: Date.now().toString(),
                title: payload.notification?.title || 'Nueva notificación',
                body: payload.notification?.body || '',
                type: (payload.data?.['type'] as 'whatsapp' | 'ticket') || 'other',
                data: payload.data,
                timestamp: new Date(),
                read: false
            };

            this.addNotification(notification);

            // Show browser notification
            this.showBrowserNotification(notification);
        });
    }

    /**
     * Show browser notification
     */
    private showBrowserNotification(notification: Notification): void {
        if (Notification.permission === 'granted') {
            new Notification(notification.title, {
                body: notification.body,
                icon: '/assets/icon/favicon.png',
                badge: '/assets/icon/badge.png',
                tag: notification.id
            });
        }
    }

    /**
     * Add notification to list
     */
    addNotification(notification: Notification): void {
        const current = this.notifications$.value;
        const updated = [notification, ...current].slice(0, 50); // Keep last 50
        this.notifications$.next(updated);
        this.updateUnreadCount();
        this.saveNotifications();
    }

    /**
     * Mark notification as read
     */
    markAsRead(id: string): void {
        const current = this.notifications$.value;
        const updated = current.map(n =>
            n.id === id ? { ...n, read: true } : n
        );
        this.notifications$.next(updated);
        this.updateUnreadCount();
        this.saveNotifications();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
        const current = this.notifications$.value;
        const updated = current.map(n => ({ ...n, read: true }));
        this.notifications$.next(updated);
        this.updateUnreadCount();
        this.saveNotifications();
    }

    /**
     * Clear all notifications
     */
    clearAll(): void {
        this.notifications$.next([]);
        this.unreadCount$.next(0);
        this.saveNotifications();
    }

    /**
     * Update unread count
     */
    private updateUnreadCount(): void {
        const count = this.notifications$.value.filter(n => !n.read).length;
        this.unreadCount$.next(count);
    }

    /**
     * Save notifications to localStorage
     */
    private saveNotifications(): void {
        localStorage.setItem('fcm_notifications', JSON.stringify(this.notifications$.value));
    }

    /**
     * Load notifications from localStorage
     */
    private loadSavedNotifications(): void {
        const saved = localStorage.getItem('fcm_notifications');
        if (saved) {
            try {
                const notifications = JSON.parse(saved).map((n: any) => ({
                    ...n,
                    timestamp: new Date(n.timestamp)
                }));
                this.notifications$.next(notifications);
                this.updateUnreadCount();
            } catch (e) {
                console.error('Error loading saved notifications');
            }
        }
    }

    /**
     * Unregister token on logout
     */
    async unregister(): Promise<void> {
        if (this.currentToken) {
            this.http.post(`${environment.apiUrl}/fcm-tokens/unregister.json`, {
                token: this.currentToken
            }).subscribe();
        }
    }
}
